import EventEmitter from 'events';
import { Socket } from 'socket.io-client';
import io from 'socket.io-client';

// Simple async delay helper (unchanged)
const delay = (delayTime: number): Promise<boolean> => {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(true), delayTime);
  });
};

const getRoomEventName = (room: string, eventName: string): string => room + ':' + eventName;

interface SocketEvent<T> {
  broadcast_group_key: string;
  event_name: string;
  message: T;
}

interface IJoinEventMessage {
  roomName: string;
  status: boolean;
  message: string;
}

export class SocketIOConnection {
  private static instance: SocketIOConnection | null = null;
  private static connecting = false;

  private readonly socket: typeof Socket;
  private readonly joinEventBus = new EventEmitter();
  private readonly eventEmitter = new EventEmitter();

  // Track rooms so we can rejoin automatically after reconnects
  private readonly joinedRooms = new Set<string>();

  private constructor(socket: typeof Socket) {
    this.socket = socket;

    socket.on('join-status', (message: string) => {
      const messageObj: IJoinEventMessage =
        typeof message === 'object' ? message : (JSON.parse(message) as IJoinEventMessage);

      this.joinEventBus.emit(messageObj.roomName, messageObj);
    });

    socket.on('disconnecting', () => {
      console.log('socket disconnecting');
    });

    socket.on('disconnect', () => {
      console.log('socket disconnected');
    });

    /**
     * Fired on initial connection and every reconnect
     */
    socket.on('connect', () => {
      console.log('socket connected');
      this.eventEmitter.emit('reconnected', true);

      // Rejoin all previously joined rooms after reconnect
      this.joinedRooms.forEach((room) => {
        this.socket.emit(
          'join',
          JSON.stringify({
            room
          })
        );
      });
    });

    // Helpful visibility for production issues
    socket.on('connect_error', (err: any) => {
      console.error('Socket connect error:', err.message);
    });

    socket.on('error', (err: any) => {
      console.error('Socket error:', err);
    });

    // Push events from backend
    socket.on('push', (data: SocketEvent<any>) => {
      const roomEventName = getRoomEventName(data.broadcast_group_key, data.event_name);
      this.eventEmitter.emit(roomEventName, data.message);
    });
  }

  public onReconnect(func: () => void): void {
    this.eventEmitter.on('reconnected', func);
  }

  public static getConnection(): Promise<SocketIOConnection> {
    const socketUrl: string = process.env.REACT_APP_SOCKET_URL ?? '';

    const getOrCreateConnection = async (resolve: any) => {
      if (SocketIOConnection.connecting) {
        // Wait for the first connection attempt to complete
        while (SocketIOConnection.connecting) {
          await delay(1000);
        }
        resolve(SocketIOConnection.instance);
        return;
      }

      if (SocketIOConnection.instance === null) {
        SocketIOConnection.connecting = true;

        /**
         * IMPORTANT:
         * - Force WebSocket transport only
         * - Prevents "Session ID unknown" in multi-instance production
         */
        const socket = io(socketUrl, {
          transports: ['websocket'],
          upgrade: false,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000
        });

        socket.on('connect', () => {
          SocketIOConnection.instance = new SocketIOConnection(socket);
          SocketIOConnection.connecting = false;
          resolve(SocketIOConnection.instance);
        });
      } else {
        resolve(SocketIOConnection.instance);
      }
    };

    return new Promise((resolve) => getOrCreateConnection(resolve));
  }

  public joinRoom(roomName: string, authKey: any): Promise<any> {
    // Save room for automatic rejoin on reconnect
    this.joinedRooms.add(roomName);

    return new Promise<any>((resolve, reject) => {
      /**
       * IMPORTANT:
       * - Do NOT send socket_id manually
       * - Backend already knows socket.id
       * - Prevents ID mismatch after reconnects
       */
      this.socket.emit(
        'join',
        JSON.stringify({
          auth_key: authKey,
          room: roomName
        })
      );

      this.joinEventBus.once(roomName, (data: IJoinEventMessage) => {
        if (data.status) {
          resolve(data.message);
        } else {
          reject(data.message);
        }
      });
    });
  }

  public removeListener(roomName: string, eventName: string): void {
    const roomEventName = getRoomEventName(roomName, eventName);
    this.eventEmitter.removeAllListeners(roomEventName);
  }

  public disconnect(): void {
    // Explicit disconnect + reset singleton
    this.socket.disconnect();
    SocketIOConnection.instance = null;
    SocketIOConnection.connecting = false;
    this.joinedRooms.clear();
  }

  public addEventListener<T>(
    roomName: string,
    eventName: string,
    callback: (message: T) => void
  ): void {
    const roomEventName = getRoomEventName(roomName, eventName);
    this.eventEmitter.on(roomEventName, callback);
  }
}
